<template>
  <div class="layout-center-wrap">
    <Auth v-if="!loading" :type="pageType" v-on:success="findInfo" />

    <header v-if="pageType === 'ID' && loading" class="member_pg_tit__wrap">
      <h1 class="member_pg_tit">아이디 찾기</h1>
      <section class="action_card__wrap">
        <article
          class="action_card"
          role="region"
          aria-labelledby="phone-title"
          aria-describedby="find-desc"
        >
          <p class="member_pg_tit__desc">
            <span v-if="!finding">
              아이디를 찾는 중입니다 잠시만 기다려 주십시오
            </span>
            <span v-else-if="finding && memberInfo">
              회원님의 아이디는 <b>{{ memberInfo }}</b> 입니다
            </span>
            <span v-else> 입력하신 정보로 가입한 정보를 찾을 수 없습니다 </span>
          </p>
          <div
            class="action_card__actions btn__wrap type-bottom"
            aria-label="이동 옵션"
          >
            <a href="/" class="btn btn--blue"> 홈페이지로 이동 </a>
          </div>
        </article>
      </section>
    </header>

    <header
      v-if="pageType === 'PASSWORD' && loading"
      class="member_pg_tit__wrap"
    >
      <h1 class="member_pg_tit" v-if="!finding">비밀번호 찾기</h1>
      <h1 class="member_pg_tit" v-if="finding">비밀번호 변경</h1>
      <section class="action_card__wrap">
        <article
          class="action_card"
          role="region"
          aria-labelledby="phone-title"
          aria-describedby="minor-desc"
        >
          <p class="member_pg_tit__desc" style="width: 100%">
            <span v-if="!finding">
              비밀번호를 찾는 중입니다 잠시만 기다려 주십시오
            </span>

            <span v-else-if="finding && memberInfo">
              <table class="form-table type-block--mobile">
                <tbody>
                  <tr>
                    <th scope="row" class="th--blue">신규 비밀번호</th>
                    <td>
                      <div class="layout-flex-row">
                        <label for="password" class="sr-only"
                          >신규 비밀번호 입력</label
                        >
                        <input
                          type="password"
                          id="password"
                          class="form-table__input"
                          v-model="newPassword"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" class="th--blue">신규 비밀번호 확인</th>
                    <td>
                      <label for="chk-password" class="sr-only"
                        >신규 비밀번호 확인 입력</label
                      >
                      <input
                        type="password"
                        id="chk-password"
                        class="form-table__input"
                        v-model="checkPassword"
                        maxlength="200"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </span>
            <span v-else> 입력하신 정보로 가입한 정보를 찾을 수 없습니다 </span>
          </p>

          <div
            class="action_card__actions btn__wrap type-bottom"
            aria-label="이동 옵션"
          >
            <a
              class="btn btn--green"
              v-if="finding && memberInfo"
              v-on:click="setNewPassword"
            >
              비밀번호 변경
            </a>
            <a href="/" class="btn btn--blue"> 홈페이지로 이동 </a>
          </div>
        </article>
      </section>
    </header>
  </div>
</template>

<script>
import { useRouter, useRoute } from "vue-router";
import { inject, ref, onMounted } from "vue";
import { useStore } from "vuex";
import Auth from "./Auth.vue";
import rsa from "@/component/utils/RSAPublicKey.js";
import dc from "@/component/utils/DataChk.js";

export default {
  components: {
    Auth,
  },
  setup() {
    const axios = inject("axios");
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const showLoading = () => store.dispatch("loading/show");
    const hideLoading = () => store.dispatch("loading/hide");

    // data 정의
    const pageType = ref(null);
    const loading = ref(false);
    const memberInfo = ref(null);
    const finding = ref(false);
    const userId = ref(null);
    const newPassword = ref(null);
    const checkPassword = ref(null);
    const encId = ref(null);
    const refId = ref(null);

    // methods 정의
    const findInfo = (findRefId, userId) => {
      encId.value = null;

      if (pageType.value === "PASSWORD") {
        if (!dc.nullChk(userId)) {
          alert("아이디를 입력 후 본인인증을 진행하여 주십시오.");
          return;
        } else {
          encId.value = rsa.encryptData(userId);
        }
      }
      if (!dc.nullChk(findRefId)) {
        alert("잘못된 접근입니다.\n초기 페이지로 이동합니다.");
        router.push("/");
        return;
      }
      refId.value = findRefId;
      loading.value = true;
      finding.value = false;
      memberInfo.value = null;

      showLoading();
      axios
        .post("/core/pu/member/find/get", {
          findType: pageType.value,
          refId: refId.value,
          id: encId.value,
        })
        .then((res) => {
          if (res.data.resType === "RES_OK") {
            finding.value = true;
            if (
              (pageType.value === "PASSWORD" && res.data.resMsg === userId) ||
              pageType.value === "ID"
            ) {
              memberInfo.value = res.data.resMsg;
            }
          } else {
            axios.setErrorSession(1003, res.data.resMsg);
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          hideLoading();
        });
    };

    // methods 정의
    const setNewPassword = () => {
      if (!dc.nullChk(newPassword.value)) {
        alert("신규 비밀번호를 입력하십시오");
        return;
      } else if (!dc.nullChk(checkPassword.value)) {
        alert("신규 비밀번호 확인을 입력하십시오");
        return;
      } else if (!dc.passwordCheck(newPassword.value)) {
        alert(
          `비밀번호는 숫자, 영문, 특수문자를 1개이상 8~20자리 이내로 입력하십시오.`
        );
        return;
      } else if (newPassword.value !== checkPassword.value) {
        alert("입력하신 비밀번호와 비밀번호 확인이 일치하지 않습니다.");
        return;
      }

      if (!dc.nullChk(encId.value)) {
        alert("아이디가 정확하지 않습니다. 다시 진행하여주십시오.");
        return;
      }

      showLoading();
      axios
        .post("/core/pu/member/new-password/set", {
          findType: pageType.value,
          refId: refId.value,
          id: encId.value,
          newPassword: rsa.encryptData(newPassword.value),
        })
        .then((res) => {
          if (res.data.resType === "RES_OK") {
            alert("비밀번호가 변경되었습니다.");
            router.push("/");
          } else {
            axios.setErrorSession(1003, res.data.resMsg);
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          hideLoading();
        });
    };

    // life cycle 정의
    onMounted(() => {
      if (!route.query.pageType) {
        router.push("/");
        return;
      }
      pageType.value = route.query.pageType;
      rsa.getPublicKey();
    });

    // html에서 사용될 데이터 정의
    return {
      pageType,
      loading,
      finding,
      memberInfo,
      findInfo,
      userId,
      newPassword,
      checkPassword,
      setNewPassword,
    };
  },
};
</script>
<style>
</style>